// src/Pages/Organization/OrgSingleProduct.tsx
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { el } from "date-fns/locale";
registerLocale("el", el);
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import useAxiosPrivate, {
	toastErrorUnlessHandled,
} from "@/hooks/useAxiosPrivate";
import useAppStore from "@/store/AppStore";
import { clampNumberInput, formatDate, MAX_NUMBER_INPUT, normalizeNumberInputDisplay } from "@/utils";
import ExtensionsPanel, { type ExtensionItem } from "@/components/ExtensionsPanel";
import ModulesTab from "@/components/ModulesTab";
import { PencilIcon, TrashIcon } from "@heroicons/react/solid";
import {
	Badge,
	Button,
	Divider,
	NumberInput,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeaderCell,
	TableRow,
} from "@tremor/react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import AddSubscription from "./AddSubscription";
import {
	type Feature,
	disableSubscriptionFeature,
	enableSubscriptionFeature,
	getAllFeatures,
} from "@/api/featuresApi";

export function checkIfDateIsBetween(
	startDate: string,
	endDate: string,
	today?: string,
) {
	if (!today) {
		today = new Date().toISOString().split("T")[0];
	}
	return today >= startDate && today <= endDate;
}

interface Subscription {
	id: number;
	org_id: number;
	nb_admin_id: number;
	package_id: number;
	allowed_users: number;
	allowed_credits: number;
	used_credits: number;
	note: string | null;
	from_date: string;
	to_date: string;
	is_active: number;
	created_at: string;
	updated_at: string;
	package_name: string;
	package_description: string | null;
	product_name: string;
	feature_keys: string[];
}

interface Product {
	id: number;
	name: string;
	description: string;
	created_at: string;
	updated_at: string;
}

interface SubscriptionRowProps {
	subscription: Subscription;
	isSelected: boolean;
	onSelect: (id: number) => void;
	onSubscriptionUpdated: () => Promise<void>;
	availableFeatures: Feature[];
	canManageModules: boolean;
	canDeleteSubscription: boolean;
}

const OrgSingleProduct: React.FC = () => {
	const [product, setProduct] = useState<Product | null>(null);
	const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
	const [tab, setTab] = useState("subscriptions");
	const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<number | null>(null);
	const [availableFeatures, setAvailableFeatures] = useState<Feature[]>([]);

	const { org_id, product_id } = useParams<{
		org_id: string;
		product_id: string;
	}>();
	const navigate = useNavigate();
	const axiosPrivate = useAxiosPrivate();
	const appStore = useAppStore() as { user: { role: string } };

	const selectedSubscription = useMemo(
		() => subscriptions.find((subscription) => subscription.id === selectedSubscriptionId),
		[selectedSubscriptionId, subscriptions],
	);

	const selectedFeatures = useMemo<ExtensionItem[]>(() => {
		if (!selectedSubscription) return [];
		const featureByKey = new Map(availableFeatures.map((f) => [f.key, f]));
		return (selectedSubscription.feature_keys ?? []).map((key) => {
			const feature = featureByKey.get(key);
			return { name: feature?.name || key, category: feature?.category };
		});
	}, [availableFeatures, selectedSubscription]);

	const lastSubscriptionEndDate = useMemo(() => {
		if (subscriptions.length === 0) {
			return undefined;
		}
		const latest = [...subscriptions].sort((a, b) =>
			b.to_date.localeCompare(a.to_date),
		)[0];
		const nextDate = new Date(latest.to_date);
		nextDate.setDate(nextDate.getDate() + 1);
		return nextDate.toISOString().split("T")[0];
	}, [subscriptions]);

	const refreshProductData = async (signal?: AbortSignal) => {
		const response = await axiosPrivate.get(
			`/auth/orgs/${org_id}/products/${product_id}`,
			signal ? { signal } : undefined,
		);
		setProduct(response.data.product);
		setSubscriptions(response.data.subscriptions);
		setSelectedSubscriptionId((currentSelectedId) => {
			if (
				currentSelectedId &&
				response.data.subscriptions.some(
					(subscription: Subscription) => subscription.id === currentSelectedId,
				)
			) {
				return currentSelectedId;
			}
			const activeSubscription =
				response.data.subscriptions.find(
					(subscription: Subscription) => Number(subscription.is_active) === 1,
				) ?? response.data.subscriptions[0];
			return activeSubscription?.id ?? null;
		});
	};

	useEffect(() => {
		let isMounted = true;
		const controller = new AbortController();

		Promise.all([
			refreshProductData(controller.signal),
			getAllFeatures(controller.signal).then((response) => {
				if (isMounted) {
					setAvailableFeatures(response.data.data);
				}
			}),
		]).catch((error) => {
			console.error(error);
			toastErrorUnlessHandled(error, "Σφάλμα κατά τη φόρτωση των δεδομένων");
		});

		return () => {
			isMounted = false;
			controller.abort();
		};
	}, [org_id, product_id]);

	return (
		<div className="flex-1 space-y-4 p-8 pt-6">
			<div className="flex items-center justify-between space-y-2">
				<h2 className="text-3xl font-bold tracking-tight">{product?.name}</h2>
				<Button onClick={() => navigate(`/org/${org_id}`)}>
					Πίσω στον Οργανισμό
				</Button>
			</div>

			<Tabs
				defaultValue="subscriptions"
				value={tab}
				onValueChange={setTab}
				className="space-y-4"
			>
				<TabsList>
					<TabsTrigger value="subscriptions">Συνδρομές</TabsTrigger>
				</TabsList>

				<TabsContent value="overview" className="space-y-4">
					<Card>
						<CardHeader>
							<CardTitle>Product Details</CardTitle>
						</CardHeader>
						<CardContent>
							<p>
								<strong>Description:</strong>{" "}
								{product?.description || "No description available"}
							</p>
							<p>
								<strong>Created At:</strong>{" "}
								{formatDate(product?.created_at || "", {
									simple: true,
									withTime: true,
								})}
							</p>
							<p>
								<strong>Updated At:</strong>{" "}
								{formatDate(product?.updated_at || "", {
									simple: true,
									withTime: true,
								})}
							</p>
						</CardContent>
					</Card>
				</TabsContent>
				<TabsContent value="subscriptions" className="space-y-4">
					<Card>
						<div className="flex">
							<div className="flex-[2] min-w-0">
								{/* Table */}
								<CardHeader>
									<CardTitle>Συνδρομές</CardTitle>
								</CardHeader>
								<CardContent>
									<AddSubscription
										productId={product_id!}
										orgId={org_id!}
										onSubscriptionAdded={() => refreshProductData()}
										lastSubscriptionEndDate={lastSubscriptionEndDate}
										defaultFeatureKeys={selectedSubscription?.feature_keys ?? []}
									/>
									<div className="mt-2 flex gap-4">
										<div className="min-w-0 flex-[2] overflow-x-auto">
											<Table>
												<TableHead>
													<TableRow>
														<TableHeaderCell>Πακέτο</TableHeaderCell>
														<TableHeaderCell>Status</TableHeaderCell>
														<TableHeaderCell>Επιτρ.Χρήστες</TableHeaderCell>
														<TableHeaderCell>Credits Συνδρομής</TableHeaderCell>
														<TableHeaderCell>Υπολειπόμενα Credits</TableHeaderCell>
														<TableHeaderCell>Από</TableHeaderCell>
														<TableHeaderCell>Έως</TableHeaderCell>
														<TableHeaderCell>Ενέργειες</TableHeaderCell>
													</TableRow>
												</TableHead>
												<TableBody>
													{subscriptions.map((subscription) => (
														<SubscriptionRow
															subscription={subscription}
															key={subscription.id}
															isSelected={selectedSubscriptionId === subscription.id}
															onSelect={setSelectedSubscriptionId}
															onSubscriptionUpdated={() => refreshProductData()}
															availableFeatures={availableFeatures}
															canManageModules={appStore.user.role === "admin" || appStore.user.role === "superAdmin"}
															canDeleteSubscription={appStore.user.role === "superAdmin"}
														/>
													))}
												</TableBody>
											</Table>
										</div>
									</div>
								</CardContent>
							</div>
							{/* Modules */}
							<div className="w-px shrink-0 self-stretch bg-gray-200 my-5" />
							<ExtensionsPanel
								features={selectedFeatures}
								totalCount={availableFeatures.length}
								title="Επεκτάσεις Συνδρομής"
								className="flex-[1] min-w-0"
							/>
						</div>

					</Card>
				</TabsContent>
			</Tabs>
		</div >
	);
};

export default OrgSingleProduct;

const SubscriptionRow: React.FC<SubscriptionRowProps> = ({
	subscription,
	isSelected,
	onSelect,
	onSubscriptionUpdated,
	availableFeatures,
	canManageModules,
	canDeleteSubscription,
}) => {
	const [editingSubscription, setEditingSubscription] =
		useState<Subscription | null>(null);
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [selectedFeatureKeys, setSelectedFeatureKeys] = useState<string[]>(
		subscription.feature_keys ?? [],
	);
	const axiosPrivate = useAxiosPrivate();

	const handleEdit = () => {
		setEditingSubscription({ ...subscription });
		setSelectedFeatureKeys(subscription.feature_keys ?? []);
		setIsDialogOpen(true);
	};

	const handleDelete = async () => {
		const isConfirmed = window.confirm(
			"Είστε σίγουροι ότι θέλετε να διαγράψετε τη συνδρομή;",
		);
		if (!isConfirmed) return;

		try {
			const response = await axiosPrivate.delete(
				`/auth/subscriptions/${subscription.id}`,
			);
			if (response.status === 204) {
				toast.success("Διαγράφτηκε με επιτυχία η συνδρομή");
				await onSubscriptionUpdated();
			}
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			console.error("Error deleting subscription:", error);
			const errorMessage =
				error?.response?.data || "Σφάλμα κατά τη διαγραφή της συνδρομής";
			toastErrorUnlessHandled(error, errorMessage);
		}
	};

	const handleInputChange = (
		field: keyof Subscription,
		value: string | number,
	) => {
		if (!editingSubscription) return;
		setEditingSubscription((prev) => {
			if (!prev) return null;
			return {
				...prev,
				[field]:
					typeof value === "number" ? clampNumberInput(value) : value,
			} as Subscription;
		});
	};

	useEffect(() => {
		if (!editingSubscription) return;
		const isActive = checkIfDateIsBetween(
			editingSubscription.from_date,
			editingSubscription.to_date,
		);
		setEditingSubscription((prev) => {
			if (!prev) return null;
			return { ...prev, is_active: isActive ? 1 : 0 } as Subscription;
		});
	}, [editingSubscription?.from_date, editingSubscription?.to_date]);

	const handleSave = async () => {
		if (!editingSubscription) return;

		if (
			editingSubscription.allowed_credits === 0 &&
			editingSubscription.used_credits > 0
		) {
			const isConfirmed = window.confirm(
				`ΠΡΟΕΙΔΟΠΟΙΗΣΗ: Θέλετε να ορίσετε τα επιτρεπόμενα credits σε 0, αλλά υπάρχουν ${editingSubscription.used_credits} χρησιμοποιημένα credits. Αυτό μπορεί να περιορίσει τη λειτουργικότητα των χρηστών. Είστε σίγουροι;`,
			);
			if (!isConfirmed) return;
		} else {
			const isConfirmed = window.confirm(
				"Είστε σίγουροι ότι θέλετε να αποθηκεύσετε τις αλλαγές;",
			);
			if (!isConfirmed) return;
		}

		setIsLoading(true);
		try {
			const response = await axiosPrivate.patch(
				`/auth/subscriptions/${editingSubscription.id}`,
				editingSubscription,
				{
					headers: { "Content-Type": "application/json" },
					withCredentials: true,
				},
			);

			const previousKeys = new Set(subscription.feature_keys ?? []);
			const nextKeys = Array.from(new Set(selectedFeatureKeys));
			const keysToEnable = nextKeys.filter((featureKey) => !previousKeys.has(featureKey));
			const keysToDisable = Array.from(previousKeys).filter(
				(featureKey) => !nextKeys.includes(featureKey),
			);

			for (const featureKey of keysToEnable) {
				await enableSubscriptionFeature(subscription.id, featureKey);
			}
			for (const featureKey of keysToDisable) {
				await disableSubscriptionFeature(subscription.id, featureKey);
			}

			setIsDialogOpen(false);
			setEditingSubscription(null);
			toast.success("Η συνδρομή ενημερώθηκε επιτυχώς");
			if (response.data?.auto_paused_users_count > 0) {
				toast.warning(
					`${response.data.auto_paused_users_count} χρήστες τέθηκαν αυτόματα σε παύση λόγω του νέου ορίου.`,
				);
			}
			await onSubscriptionUpdated();
		} catch (error) {
			console.error("Error updating subscription:", error);
			toastErrorUnlessHandled(
				error,
				"Σφάλμα κατά την αποθήκευση των αλλαγών",
			);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<TableRow
			key={subscription.id}
			onClick={() => onSelect(subscription.id)}
			className={`cursor-pointer ${isSelected ? "bg-blue-50" : "hover:bg-gray-50"}`}
		>
			<TableCell>{subscription.package_name}</TableCell>
			<TableCell>
				<Badge color={subscription.is_active ? "green" : "gray"}>
					{subscription.is_active ? "Ενεργή" : "Ανενεργή"}
				</Badge>
			</TableCell>
			<TableCell>{subscription.allowed_users}</TableCell>
			<TableCell>
				<span
					className={
						subscription.allowed_credits === 0
							? "font-semibold text-red-600"
							: ""
					}
				>
					{subscription.allowed_credits}
				</span>
			</TableCell>
			<TableCell>
				<span
					className={
						subscription.allowed_credits - subscription.used_credits <= 0
							? "font-semibold text-red-600"
							: ""
					}
				>
					{subscription.allowed_credits - subscription.used_credits}
				</span>
			</TableCell>
			<TableCell>
				{formatDate(subscription.from_date, { simple: true, withTime: false })}
			</TableCell>
			<TableCell>
				{formatDate(subscription.to_date, { simple: true, withTime: false })}
			</TableCell>
			<TableCell>
				<Button
					icon={PencilIcon}
					variant="secondary"
					onClick={(event) => {
						event.stopPropagation();
						handleEdit();
					}}
				>
					Edit
				</Button>
				<Dialog
					open={isDialogOpen}
					onOpenChange={(open) => {
						setIsDialogOpen(open);
						if (!open) {
							setEditingSubscription(null);
						}
					}}
				>
					<DialogContent
						className="flex h-[700px] max-h-[95vh] w-[600px] max-w-[95vw] flex-col overflow-hidden"
						onPointerDownOutside={(event) => event.preventDefault()}
					>
						<DialogHeader>
							<DialogTitle>Επεξεργασία Συνδρομής</DialogTitle>
						</DialogHeader>
						<form
							onSubmit={(event) => {
								event.preventDefault();
								handleSave();
							}}
							className="flex min-h-0 flex-1 flex-col gap-4"
						>
							<Tabs defaultValue="sub" className="flex min-h-0 flex-1 flex-col">
								<TabsList className="w-full shrink-0">
									<TabsTrigger value="sub" className="flex-1">
										Στοιχεία Συνδρομής
									</TabsTrigger>
									{canManageModules && (
										<TabsTrigger value="ext" className="flex-1">
											Επεκτάσεις
										</TabsTrigger>
									)}
								</TabsList>

								<TabsContent
									value="sub"
									className="mt-4 flex-1 space-y-4 overflow-y-auto pr-1"
								>
									<div className="grid grid-cols-2 gap-4">
										<div>
											<label
												htmlFor="allowed_users"
												className="block text-sm font-medium text-gray-700"
											>
												Επιτρεπόμενοι Χρήστες
											</label>
											<NumberInput
												id="allowed_users"
												placeholder="Allowed Users"
												value={editingSubscription?.allowed_users || 0}
												onValueChange={(value) =>
													handleInputChange(
														"allowed_users",
														clampNumberInput(value, { min: 1 }),
													)
												}
												onChange={(event) => {
													event.target.value = normalizeNumberInputDisplay(
														event.target.value,
														{ min: 1 },
													);
												}}
												min={1}
												max={MAX_NUMBER_INPUT}
											/>
										</div>
										<div>
											<label
												htmlFor="allowed_credits"
												className="block text-sm font-medium text-gray-700"
											>
												Επιτρεπόμενα Credits
											</label>
											<NumberInput
												id="allowed_credits"
												placeholder="Allowed Credits"
												value={editingSubscription?.allowed_credits || 0}
												onValueChange={(value) => handleInputChange("allowed_credits", value)}
												onChange={(event) => {
													event.target.value = normalizeNumberInputDisplay(event.target.value);
												}}
												min={0}
												max={MAX_NUMBER_INPUT}
											/>
											{editingSubscription?.allowed_credits === 0 && (
												<p className="text-sm text-orange-600">
													Προσοχή: Ορίζεται σε 0 credits
												</p>
											)}
											{editingSubscription?.allowed_credits !== undefined &&
												editingSubscription.allowed_credits < subscription.used_credits && (
													<p className="text-sm text-red-600">
														Δεν μπορεί να είναι μικρότερο από τα χρησιμοποιημένα (
														{subscription.used_credits})
													</p>
												)}
										</div>
									</div>

									<div>
										<label htmlFor="to_date" className="block text-sm font-medium text-gray-700">
											Ημερομηνία Λήξης
										</label>
										<DatePicker
											id="to_date"
											selected={
												editingSubscription?.to_date
													? new Date(editingSubscription.to_date)
													: undefined
											}
											onChange={(date) =>
												handleInputChange(
													"to_date",
													date ? date.toISOString().split("T")[0] : "",
												)
											}
											locale="el"
											dateFormat="dd/MM/yyyy"
											placeholderText="Επιλέξτε Ημερομηνία"
											showYearDropdown
											yearDropdownItemNumber={40}
											scrollableYearDropdown
											className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
										/>
									</div>
								</TabsContent>

								{canManageModules && (
									<TabsContent
										value="ext"
										className="mt-4 flex-1 overflow-y-auto pr-1"
									>
										<ModulesTab
											modules={availableFeatures}
											selectedKeys={selectedFeatureKeys}
											setSelectedKeys={setSelectedFeatureKeys}
										/>
									</TabsContent>
								)}
							</Tabs>

							<Divider className="shrink-0" />

							<div className="flex shrink-0 justify-center space-x-4">
								<Button type="submit" color="emerald" loading={isLoading} className="w-2/3">
									Αποθήκευση
								</Button>
								<Button
									type="button"
									color="red"
									onClick={() => {
										setIsDialogOpen(false);
										setEditingSubscription(null);
									}}
									className="w-1/3"
								>
									Ακύρωση
								</Button>
							</div>
						</form>
					</DialogContent>
				</Dialog>
			</TableCell>
			{canDeleteSubscription && (
				<TableCell>
					<Button
						icon={TrashIcon}
						variant="primary"
						color="red"
						onClick={(event) => {
							event.stopPropagation();
							handleDelete();
						}}
					>
						Διαγραφή
					</Button>
				</TableCell>
			)}
		</TableRow>
	);
};
